import { useState } from "react";
import { FlaskConical, Plus, Search } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { evalCasePath, type PartialEvalCase } from "~/services/evals";
import { CaseHealthMatrix } from "./CaseHealthMatrix";
import { ConfirmDeleteDialog } from "./ConfirmDeleteDialog";
import { TableSkeleton } from "./EvalSkeletons";
import { useTableSort } from "./TableSort";
import { CASE_SORT_DIRECTIONS, CaseSortKey } from "./constants/sorting";
import type { DatasetRefs } from "./primitives/chips";
import { ShownCount } from "./primitives/notices";
import { type CaseHealthCell, type CaseHealthRow, sortCaseRows } from "./selectors/cases";

/** Roughly the table's columns, so the placeholder stands where the table will. */
const CASE_COLUMN_WIDTHS = ["w-48", "w-28", "w-28", "w-16", "w-20", "w-56", "w-8"];

interface EvalCasesTabProps {
  rows: CaseHealthRow[];
  /** How deep each case's history goes, for the subtitle. Not a column count. */
  runsPerCase: number;
  /** Catalog datasets by id, for the dataset column. */
  datasets: DatasetRefs;
  /** Cases in the suite before the search box narrowed them, for the count. */
  totalCaseCount: number;
  /** Free-text narrowing of this table, undebounced so the box stays responsive. */
  query: string;
  onQueryChange: (query: string) => void;
  /** Recent runs the caller cannot read, absent from every row. */
  hiddenRunCount: number;
  isLoading: boolean;
  onSelectCase: (caseId: string) => void;
  onSelectCell: (row: CaseHealthRow, cell: CaseHealthCell) => void;
  onNewCase: () => void;
  onDeleted: () => void;
}

/**
 * Every case in the suite: its tags and dataset, then its health as the columns —
 * pass rate, flakiness and its own latest runs. What a case is configured to do
 * lives in the case drawer, beside the runs of the same case.
 *
 * The search box lives here rather than in the page's filter row because it acts
 * on this table alone, while the facets in that row reach both resources.
 * Beside the count it changes, it also states its own scope: it narrows these
 * rows, not the suite the KPIs measure.
 */
export const EvalCasesTab = ({
  rows,
  runsPerCase,
  datasets,
  totalCaseCount,
  query,
  onQueryChange,
  hiddenRunCount,
  isLoading,
  onSelectCase,
  onSelectCell,
  onNewCase,
  onDeleted,
}: EvalCasesTabProps) => {
  // Lowest pass rate first, so the cases a reviewer came to look at are on top.
  const sort = useTableSort<CaseSortKey>(CaseSortKey.PASS_RATE, "asc", CASE_SORT_DIRECTIONS);
  const sorted = sortCaseRows(rows, sort.sortKey, sort.direction);
  const [pendingDelete, setPendingDelete] = useState<PartialEvalCase | null>(null);

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 pb-4">
        <div>
          <div className="flex items-baseline gap-2">
            <CardTitle className="text-base font-semibold">Cases</CardTitle>
            <ShownCount shown={rows.length} total={totalCaseCount} noun="case" />
          </div>
          {/* "Its own last N" rather than "the last N runs": the window is per
              case, so two rows can cover entirely different runs, and a reader
              who assumes otherwise will read down a column and draw the wrong
              conclusion. */}
          <p className="mt-0.5 text-sm text-muted-foreground">
            Mean judge score per case, across its own last {runsPerCase} runs.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <div className="relative w-[220px]">
            <Search
              className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <Input
              value={query}
              onChange={(event) => onQueryChange(event.target.value)}
              placeholder="Filter cases…"
              className="h-9 pl-9"
              aria-label="Filter cases by name or description"
            />
          </div>
          <Button onClick={onNewCase}>
            <Plus className="size-4" aria-hidden />
            New case
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading && rows.length === 0 ? (
          <TableSkeleton columns={CASE_COLUMN_WIDTHS} />
        ) : rows.length === 0 ? (
          <EmptyCases onNewCase={onNewCase} />
        ) : (
          <CaseHealthMatrix
            rows={sorted}
            datasets={datasets}
            sort={sort}
            hiddenRunCount={hiddenRunCount}
            onSelectCase={onSelectCase}
            onSelectCell={onSelectCell}
            onDeleteCase={setPendingDelete}
          />
        )}
      </CardContent>

      <ConfirmDeleteDialog
        open={pendingDelete != null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title={`Delete ${pendingDelete?.name ?? "this case"}?`}
        description={
          <>
            The case and its definition go for good. Results already recorded in past runs stay, but
            lose their definition.
          </>
        }
        confirmLabel="Delete case"
        path={pendingDelete ? evalCasePath(pendingDelete.id) : null}
        errorMessage="Could not delete the case."
        onDeleted={onDeleted}
      />
    </Card>
  );
};

/**
 * Empty state that teaches the surface: cases are imported from the YAML suites,
 * so "no cases" usually means the import has not run here. Reached only once the
 * suite has loaded, so it can state a finding rather than hedge.
 */
const EmptyCases = ({ onNewCase }: { onNewCase: () => void }) => (
  <div className="flex flex-col items-center gap-2 py-12 text-center">
    <FlaskConical className="size-8 text-muted-foreground/60" aria-hidden />
    <p className="text-sm font-medium text-foreground">No eval cases match this filter</p>
    <p className="max-w-md text-sm text-muted-foreground">
      Cases usually arrive from the suites under{" "}
      <code className="font-mono text-xs">tests/evals/configs</code> via the importer. You can also
      write one here.
    </p>
    <Button variant="outline" onClick={onNewCase} className="mt-2">
      <Plus className="size-4" aria-hidden />
      New case
    </Button>
  </div>
);
